import React from 'react'

import { env_url } from './endpoints'
import {
    getPlayerImageURL,
    getPlayerLink,
    formatNumberWithDecimal,
    sortNumberText,
    formatGameTeamDetails,
    getPlayerLinkWithNumberAndPosition,
    formatDivisionRanksNumericValue,
    getTeamLogo,
    formatPlayerLink,
    sortByPlayerElement,
} from './shared_helpers.js'
import { handleError } from 'utils/js/error_helpers'
import update from 'immutability-helper'
import { preserveQueryParams } from './browser_helpers'
import { useIsDark } from '../../context/DarkContext.js'

export const NBAsilhouette = `${env_url}/player-silhouette-hockey-cut.png`

export const imgErrNBA = ev => {
    ev.target.src = NBAsilhouette
}

export const imgErrNBASVG = ev => {
    try {
        ev.target.href.baseVal = NBAsilhouette
    } catch (e) {
        handleError(e)
    }
}

export const WNBAsilhouette = `${env_url}/player-silhouette-female.svg`

export const imgErrWNBA = ev => {
    ev.target.src = WNBAsilhouette
}

export const imgErrWNBASVG = ev => {
    try {
        ev.target.href.baseVal = WNBAsilhouette
    } catch (e) {
        handleError(e)
    }
}

/*
 * formatQuarterName: This function returns the text label of a quarter based on it's numerical value
 *
 * @param {int} quarter - integer corresponding to a quarter
 * @return {string} quarter - text representing the period in plain english
 */
export const formatQuarterName = quarter => {
    switch (quarter) {
        case 1:
            return '1st'
        case 2:
            return '2nd'
        case 3:
            return '3rd'
        case 4:
            return '4th'
        case 5:
            return 'OT'
        default:
            return quarter - 4 + 'OT'
    }
}

export const labelQuarters = quarters => {
    let quartersCopy = quarters.slice(0)

    quartersCopy.forEach((quarter, i) => {
        if (i < 4) {
            quarter.label = i + 1
        } else if (i === 4) {
            quarter.label = 'OT'
        } else {
            quarter.label = i - 3 + 'OT'
        }
    })

    return quartersCopy
}

export const nbaPriorityStat = (blocked_shots, three_pointers_made, steals) => {
    return blocked_shots > three_pointers_made
        ? blocked_shots > steals
            ? { key: 'BLK', val: blocked_shots }
            : { key: 'STL', val: steals }
        : three_pointers_made > steals
        ? { key: '3PT', val: three_pointers_made }
        : { key: 'STL', val: steals }
}

export const nbaTeamNameIDArrayMap = new Map([
    ['583ecb8f-fb46-11e1-82cb-f4ce4684ea4c', 'atlanta-hawks'],
    ['583eccfa-fb46-11e1-82cb-f4ce4684ea4c', 'boston-celtics'],
    ['583ecc9a-fb46-11e1-82cb-f4ce4684ea4c', 'new-orleans-pelicans'],
    ['583ec5fd-fb46-11e1-82cb-f4ce4684ea4c', 'chicago-bulls'],
    ['583ec773-fb46-11e1-82cb-f4ce4684ea4c', 'cleveland-cavaliers'],
    ['583ecf50-fb46-11e1-82cb-f4ce4684ea4c', 'dallas-mavericks'],
    ['583ed102-fb46-11e1-82cb-f4ce4684ea4c', 'denver-nuggets'],
    ['583ec928-fb46-11e1-82cb-f4ce4684ea4c', 'detroit-pistons'],
    ['583ec825-fb46-11e1-82cb-f4ce4684ea4c', 'golden-state-warriors'],
    ['583ecb3a-fb46-11e1-82cb-f4ce4684ea4c', 'houston-rockets'],
    ['583ec7cd-fb46-11e1-82cb-f4ce4684ea4c', 'indiana-pacers'],
    ['583ecdfb-fb46-11e1-82cb-f4ce4684ea4c', 'los-angeles-clippers'],
    ['583ecae2-fb46-11e1-82cb-f4ce4684ea4c', 'los-angeles-lakers'],
    ['583ecea6-fb46-11e1-82cb-f4ce4684ea4c', 'miami-heat'],
    ['583ecefd-fb46-11e1-82cb-f4ce4684ea4c', 'milwaukee-bucks'],
    ['583eca2f-fb46-11e1-82cb-f4ce4684ea4c', 'minnesota-timberwolves'],
    ['583ec9d6-fb46-11e1-82cb-f4ce4684ea4c', 'brooklyn-nets'],
    ['583ec70e-fb46-11e1-82cb-f4ce4684ea4c', 'new-york-knicks'],
    ['583ed157-fb46-11e1-82cb-f4ce4684ea4c', 'orlando-magic'],
    ['583ec87d-fb46-11e1-82cb-f4ce4684ea4c', 'philadelphia-76ers'],
    ['583ecfa8-fb46-11e1-82cb-f4ce4684ea4c', 'phoenix-suns'],
    ['583ed056-fb46-11e1-82cb-f4ce4684ea4c', 'portland-trail-blazers'],
    ['583ed0ac-fb46-11e1-82cb-f4ce4684ea4c', 'sacramento-kings'],
    ['583ecd4f-fb46-11e1-82cb-f4ce4684ea4c', 'san-antonio-spurs'],
    ['583ecfff-fb46-11e1-82cb-f4ce4684ea4c', 'oklahoma-city-thunder'],
    ['583ece50-fb46-11e1-82cb-f4ce4684ea4c', 'utah-jazz'],
    ['583ec8d4-fb46-11e1-82cb-f4ce4684ea4c', 'washington-wizards'],
    ['583ecda6-fb46-11e1-82cb-f4ce4684ea4c', 'toronto-raptors'],
    ['583eca88-fb46-11e1-82cb-f4ce4684ea4c', 'memphis-grizzlies'],
    ['583ec97e-fb46-11e1-82cb-f4ce4684ea4c', 'charlotte-hornets'],
])

export function formatLastNineEvents(lastNineEvents = [], homeTeam, visitingTeam, league) {
    const isDarkMode = useIsDark()
    const ghostPlays = [
        { id: 1, isGhost: true },
        { id: 2, isGhost: true },
        { id: 3, isGhost: true },
        { id: 4, isGhost: true },
        { id: 5, isGhost: true },
        { id: 6, isGhost: true },
        { id: 7, isGhost: true },
        { id: 8, isGhost: true },
        { id: 9, isGhost: true },
    ]

    if (!lastNineEvents) return ghostPlays

    const massagedPlays = lastNineEvents.map(play => {
        const formattedPlayDescription = play.description.replace('60 second timeout', 'timeout')
        const teamLogo =
            play.team_id === homeTeam.id
                ? getTeamLogo(homeTeam, isDarkMode, 'image_url_90')
                : getTeamLogo(visitingTeam, isDarkMode, 'image_url_90')

        return {
            id: play.id,
            title: setPlayTitle(play),
            color: getPlayColor(play.event),
            bodyText: shortenPlayerNames(
                formattedPlayDescription,
                play.player_display_name,
                play.position
            ),
            teamLogo: teamLogo,
            showPlayerHeadshot: play.player_id !== 0 ? true : false,
            playerHeadshot: play.player_id ? setPlayerHeadshot(play.player_id) : null,
            playerHeadshotError: league === 'WNBA' ? imgErrWNBA : imgErrNBA,
        }
    })

    return massagedPlays.concat(ghostPlays.slice(massagedPlays.length))
}

const eventNameMap = new Map([
    ['rebound', 'REB'],
    ['twopointmade', 'FG'],
    ['threepointmade', 'FG'],
    ['freethrowmade', 'FT'],
    ['lineupchange', 'SUB'],
    ['delay', 'FOUL'],
    ['defensivegoaltending', 'FOUL'],
    ['flagrantone', 'FOUL'],
    ['flagranttwo', 'FOUL'],
    ['offensivefoul', 'FOUL'],
    ['clearpathfoul', 'FOUL'],
    ['shootingfoul', 'FOUL'],
    ['personalfoul', 'FOUL'],
    ['technicalfoul', 'FOUL'],
    ['technicalfoulnonunsportsmanlike', 'FOUL'],
    ['turnover', 'TO'],
    ['challengetimeout', 'TIMEOUT'],
    ['officialtimeout', 'TIMEOUT'],
    ['tvtimeout', 'TIMEOUT'],
    ['teamtimeout', 'TIMEOUT'],
    ['requestreview', 'REVIEW'],
    ['review', 'REVIEW'],
    ['stoppage', 'STOPPAGE'],
    ['jumpball', 'JUMP'],
    ['ejection', 'EJECT'],
    ['challengereview', 'REPLAY'],
    ['lane', 'VIOL'],
    ['kickball', 'VIOL'],
    ['doublelane', 'VIOL'],
    ['defaultviolation', 'VIOL'],
    ['jumpballviolation', 'VIOL'],
])

function setPlayTitle(play) {
    /* insert logic to rename events if needed */
    let formattedEvent = eventNameMap.get(play.event)
    let formattedQuarter = formatQuarterName(play.quarter)

    if (formattedEvent === 'SUB' && play.time === '12:00') {
        formattedEvent = 'STARTER'
    }

    formattedEvent = `${formattedEvent} - ${play.time} / ${formattedQuarter}`

    return formattedEvent || ''
}

function shortenPlayerNames(originalText, playerDisplayName, playerPosition) {
    let newText = originalText

    if (newText === 'Starting Lineup') {
        newText = `${playerDisplayName} - ${playerPosition}`
    }

    return newText
}

function getPlayColor(eventType) {
    const darkBlue = 'darkBlue'
    const red = 'red'
    const grey = 'grey'
    const brightBlue = 'brightBlue'

    switch (eventType) {
        case 'twopointmade':
        case 'threepointmade':
        case 'freethrowmade':
        case 'owngoal':
        case 'penaltygoal':
        case 'shootoutgoal':
        case 'shotsaved':
            return darkBlue
        case 'offensivefoul':
        case 'clearpathfoul':
        case 'shootingfoul':
        case 'personalfoul':
        case 'technicalfoul':
        case 'technicalfoulnonunsportsmanlike':
        case 'ejection':
        case 'jumpballviolation':
        case 'lane':
        case 'officialtimeout':
        case 'tvtimeout':
        case 'teamtimeout':
        case 'challengetimeout':
        case 'lineupchange':
        case 'defaultviolation':
        case 'doublelane':
        case 'kickball':
        case 'delay':
        case 'defensivegoaltending':
        case 'flagrantone':
        case 'flagranttwo':
            return red
        case 'rebound':
        case 'review':
        case 'turnover':
        case 'challengereview':
        case 'requestreview':
        case 'stoppage':
            return brightBlue
        default:
            return grey
    }
}

function setPlayerHeadshot(playerID) {
    return getPlayerImageURL(playerID, 'xs')
}

export function formatInjuries(teamInjuries = []) {
    if (teamInjuries === null) {
        return []
    }

    const formattedInjuries = teamInjuries.map(injury => {
        return {
            playerName: getPlayerLink(injury),
            playerPosition: injury.position,
            type: `${injury.type.charAt(0).toUpperCase()}${injury.type.slice(1)}`,
            status: injury.status,
            timestamp: injury.timestamp,
            emptyColumn: ' ',
        }
    })

    return formattedInjuries.sort((a, b) => b.timestamp - a.timestamp)
}

export function formatSeasonLeaders(homeTeam, visitingTeam) {
    const leaderCategories = [
        {
            title: 'Points',
            categoryKey: 'points_per_game_leader',
            valueKey: 'total',
        },
        {
            title: 'Assists',
            categoryKey: 'assists_per_game_leader',
            valueKey: 'total',
        },
        {
            title: 'Rebounds',
            categoryKey: 'rebounds_per_game_leader',
            valueKey: 'total',
        },
        {
            title: 'FG%',
            categoryKey: 'field_goal_percentage_leader',
            valueKey: 'total',
            isPercentage: true,
        },
        {
            title: 'FT%',
            categoryKey: 'free_throw_percentage_leader',
            valueKey: 'total',
            isPercentage: true,
        },
        {
            title: '3PT%',
            categoryKey: 'three_point_percentage_leader',
            valueKey: 'total',
            isPercentage: true,
        },
    ]

    const seasonLeaders = leaderCategories.map(leaderCategory => {
        const homeTeamData = homeTeam?.season_leaders?.[leaderCategory.categoryKey]
        const visitingTeamData = visitingTeam?.season_leaders?.[leaderCategory.categoryKey]

        if (!homeTeamData || !visitingTeamData) {
            return null
        }

        let homeTeamValue = homeTeamData[leaderCategory.valueKey]
        let visitingTeamValue = visitingTeamData[leaderCategory.valueKey]

        if (leaderCategory.isPercentage) {
            homeTeamValue = (homeTeamValue * 100).toFixed(1)
            visitingTeamValue = (visitingTeamValue * 100).toFixed(1)
        }

        return {
            title: leaderCategory.title,
            homeTeamShortName: homeTeam.short_name,
            homeTeamColor: homeTeam.color,
            visitingTeamColor: visitingTeam.color,
            visitingTeamShortName: visitingTeam.short_name,
            homeTeamPlayerImage: homeTeamData.image_urls.xs,
            visitingTeamPlayerImage: visitingTeamData.image_urls.xs,
            homeTeamLogo: homeTeam.image_url_90,
            visitingTeamLogo: visitingTeam.image_url_90,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            homeTeamPlayerName: `${homeTeamData.first_name.charAt(0)}. ${homeTeamData.last_name}`,
            visitingTeamPlayerName: `${visitingTeamData.first_name.charAt(0)}. ${
                visitingTeamData.last_name
            }`,
            homeTeamDetails: {
                first_name: homeTeamData.first_name,
                last_name: homeTeamData.last_name,
                id: homeTeamData.id,
            },
            visitingTeamDetails: {
                first_name: visitingTeamData.first_name,
                last_name: visitingTeamData.last_name,
                id: visitingTeamData.id,
            },
        }
    })
    return seasonLeaders.filter(n => n) // filter out null values
}

export function formatTeamStats(homeTeam, visitingTeam) {
    if (!visitingTeam.season_stats || !homeTeam.season_stats) {
        return []
    }

    const statCategories = [
        {
            title: 'Points Per Game',
            valueKey: 'points_per_game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
        },
        {
            title: 'Points Allowed Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'points_allowed_per_game',
        },
        {
            title: 'FG%',
            valueFormatter: n => formatNumberWithDecimal(n * 100, 1),
            valueKey: 'field_goal_percent',
            isPercent: true,
        },
        {
            title: '3PT%',
            valueFormatter: n => formatNumberWithDecimal(n * 100, 1),
            valueKey: 'three_point_shooting_percent',
            isPercent: true,
        },
        {
            title: 'FT%',
            valueFormatter: n => formatNumberWithDecimal(n * 100, 1),
            valueKey: 'free_throw_pct',
            isPercent: true,
        },
        {
            title: 'Turnovers Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'turnovers_per_game',
        },
        {
            title: 'Rebounds Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'rebounds_per_game',
        },
        {
            title: 'Assists Per Game',
            valueFormatter: n => formatNumberWithDecimal(n, 1),
            valueKey: 'assists_per_game',
        },
    ]

    const teamStats = statCategories.map(statCategory => {
        let homeTeamValue = homeTeam.season_stats[statCategory.valueKey]
        let visitingTeamValue = visitingTeam.season_stats[statCategory.valueKey]

        if (
            (!homeTeamValue && homeTeamValue !== 0) ||
            (!visitingTeamValue && visitingTeamValue !== 0)
        ) {
            return null
        }

        return {
            title: statCategory.title,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            isPercent: statCategory.isPercent,
            valueFormatter: statCategory.valueFormatter,
        }
    })
    return teamStats.filter(n => n) // filter out null values
}

export function formatGameStats(homeTeam, visitingTeam) {
    if (!visitingTeam.game_stats || !homeTeam.game_stats) {
        return []
    }

    const statCategories = [
        {
            title: 'FG%',
            valueKey: 'field_goals_pct',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
            isPercent: true,
        },
        {
            title: '3PT%',
            valueKey: 'three_point_pct',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
            isPercent: true,
        },
        {
            title: 'FT%',
            valueKey: 'free_throws_pct',
            valueFormatter: n => `${formatNumberWithDecimal(n, 1)}`,
            isPercent: true,
        },
        {
            title: 'Turnovers',
            valueKey: 'turnovers',
        },
        {
            title: 'Rebounds',
            valueKey: 'rebounds_total',
        },
        {
            title: 'Assists',
            valueKey: 'assists',
        },
    ]

    const gameStats = statCategories.map(statCategory => {
        let homeTeamValue = homeTeam.game_stats[statCategory.valueKey]
        let visitingTeamValue = visitingTeam.game_stats[statCategory.valueKey]

        if (typeof homeTeamValue === 'string' || homeTeamValue instanceof String) {
            homeTeamValue = parseFloat(homeTeamValue) || 0
        }
        if (typeof visitingTeamValue === 'string' || visitingTeamValue instanceof String) {
            visitingTeamValue = parseFloat(visitingTeamValue) || 0
        }

        if (statCategory.isPercent) {
            homeTeamValue = homeTeamValue * 100
            visitingTeamValue = visitingTeamValue * 100
        }

        if (
            (!homeTeamValue && homeTeamValue !== 0) ||
            (!visitingTeamValue && visitingTeamValue !== 0)
        ) {
            return null
        }

        return {
            title: statCategory.title,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            valueFormatter: statCategory.valueFormatter,
            isPercent: statCategory.isPercent,
        }
    })
    return gameStats.filter(n => n) // filter out null values
}

export function formatSeasonRecord(team) {
    if (team?.team_standings?.wins >= 0 && team?.team_standings?.losses >= 0) {
        return `${team.team_standings.wins}-${team.team_standings.losses}`
    } else {
        return ''
    }
}

export function formatTimeoutsAndFouls(team, fouls) {
    if (!isNaN(team?.full_timeouts_left) && !isNaN(fouls))
        return `TO: ${team?.full_timeouts_left} | TF: ${fouls}`
    if (!isNaN(team?.full_timeouts_left)) return `TO: ${team?.full_timeouts_left}`
    if (!isNaN(fouls)) return `TF: ${fouls}`
    return ''
}

export function formatQuarters(quarters) {
    const quarterTotal = 4
    let defaultQuarters = []
    for (let i = 0; i < quarterTotal; i++) {
        defaultQuarters = [
            ...defaultQuarters,
            {
                visitingTeamScore: '-',
                homeTeamScore: '-',
                label: '',
            },
        ]
    }
    let filledQuarters = quarters ? update(defaultQuarters, { $merge: quarters }) : []
    let labelledQuarters = labelQuarters(filledQuarters)

    return [filledQuarters, labelledQuarters]
}
export function sortByDashNumbers(a = '0-0', b = '0-0') {
    const firstValue = parseInt(a.split('-')[0])
    const secondValue = parseInt(b.split('-')[0])
    if (firstValue < secondValue) {
        return -1
    }
    if (firstValue > secondValue) {
        return 1
    }
    return 0
}
export function formatBoxscore(team = {}) {
    const { starters = [], bench = [], boxscore_totals = {} } = team
    return [
        {
            title: 'Starters',
            columns: [
                {
                    title: 'MIN',
                    dataKey: 'minutes',
                    tooltipText: 'Minutes',
                    sortFunction: sortNumberText,
                },
                { title: 'PTS', dataKey: 'points', tooltipText: 'Points' },
                { title: 'REB', dataKey: 'rebounds', tooltipText: 'Rebounds' },
                { title: 'AST', dataKey: 'assists', tooltipText: 'Assists' },
                { title: 'PF', dataKey: 'personalFouls', tooltipText: 'Personal Fouls' },
                {
                    title: 'FG',
                    dataKey: 'fieldGoals',
                    tooltipText: 'Field Goals',
                    sortFunction: sortByDashNumbers,
                },
                {
                    title: '3PT',
                    dataKey: 'threePointers',
                    tooltipText: 'Three Pointers',
                    sortFunction: sortByDashNumbers,
                },
                {
                    title: 'FT',
                    dataKey: 'freeThrows',
                    tooltipText: 'Free Throws',
                    sortFunction: sortByDashNumbers,
                },
                { title: 'STL', dataKey: 'steals', tooltipText: 'Steals' },
                { title: 'BLK', dataKey: 'blocks', tooltipText: 'Blocked Shots' },
                {
                    title: 'OREB',
                    dataKey: 'offensiveRebounds',
                    tooltipText: 'Offensive Rebounds',
                },
                { title: 'DREB', dataKey: 'defensiveRebounds', tooltipText: 'Defensive Rebounds' },

                { title: 'TO', dataKey: 'turnovers', tooltipText: 'Turnovers' },
            ],
            data: starters?.map(player => {
                return {
                    name: getPlayerLinkWithNumberAndPosition(player),
                    minutes: player.mins,
                    points: player.points,
                    rebounds: player.rebounds_total,
                    assists: player.assists,
                    steals: player.steals,
                    blocks: player.blocked_shots,
                    fieldGoals: player.field_goals_made + '-' + player.field_goals_attempted,
                    threePointers:
                        player.three_pointers_made + '-' + player.three_pointers_attempted,
                    freeThrows: player.free_throws_made + '-' + player.free_throws_attempted,
                    offensiveRebounds: player.offensive_rebounds,
                    defensiveRebounds: player.defensive_rebounds,
                    personalFouls: player.personal_fouls,
                    turnovers: player.turnovers,
                }
            }),
        },
        {
            title: 'Bench',
            columns: [
                {
                    title: 'MIN',
                    dataKey: 'minutes',
                    tooltipText: 'Minutes',
                    sortFunction: sortNumberText,
                },
                { title: 'PTS', dataKey: 'points', tooltipText: 'Points' },
                { title: 'REB', dataKey: 'rebounds', tooltipText: 'Rebounds' },
                { title: 'AST', dataKey: 'assists', tooltipText: 'Assists' },
                { title: 'PF', dataKey: 'personalFouls', tooltipText: 'Personal Fouls' },
                { title: 'FG', dataKey: 'fieldGoals', tooltipText: 'Field Goals' },
                { title: '3PT', dataKey: 'threePointers', tooltipText: 'Three Pointers' },
                { title: 'FT', dataKey: 'freeThrows', tooltipText: 'Free Throws' },
                { title: 'STL', dataKey: 'steals', tooltipText: 'Steals' },
                { title: 'BLK', dataKey: 'blocks', tooltipText: 'Blocked Shots' },
                { title: 'OREB', dataKey: 'offensiveRebounds', tooltipText: 'Offensive Rebounds' },
                { title: 'DREB', dataKey: 'defensiveRebounds', tooltipText: 'Defensive Rebounds' },

                { title: 'TO', dataKey: 'turnovers', tooltipText: 'Turnovers' },
            ],
            data: bench
                .filter(player => player.reason === '')
                .map(player => {
                    return {
                        name: getPlayerLinkWithNumberAndPosition(player),
                        minutes: player.mins,
                        points: player.points,
                        rebounds: player.rebounds_total,
                        assists: player.assists,
                        steals: player.steals,
                        blocks: player.blocked_shots,
                        fieldGoals: player.field_goals_made + '-' + player.field_goals_attempted,
                        threePointers:
                            player.three_pointers_made + '-' + player.three_pointers_attempted,
                        freeThrows: player.free_throws_made + '-' + player.free_throws_attempted,
                        offensiveRebounds: player.offensive_rebounds,
                        defensiveRebounds: player.defensive_rebounds,
                        personalFouls: player.personal_fouls,
                        turnovers: player.turnovers,
                    }
                }),
        },
        {
            title: 'Team Totals',
            columns: [
                {
                    title: 'MIN',
                    dataKey: 'minutes',
                    tooltipText: 'Minutes',
                },
                { title: 'PTS', dataKey: 'points', tooltipText: 'Points' },
                { title: 'REB', dataKey: 'rebounds', tooltipText: 'Rebounds' },
                { title: 'AST', dataKey: 'assists', tooltipText: 'Assists' },
                { title: 'PF', dataKey: 'personalFouls', tooltipText: 'Personal Fouls' },
                { title: 'FG', dataKey: 'fieldGoals', tooltipText: 'Field Goals' },
                { title: '3PT', dataKey: 'threePointers', tooltipText: 'Three Pointers' },
                { title: 'FT', dataKey: 'freeThrows', tooltipText: 'Free Throws' },
                { title: 'STL', dataKey: 'steals', tooltipText: 'Steals' },
                { title: 'BLK', dataKey: 'blocks', tooltipText: 'Blocked Shots' },
                { title: 'OREB', dataKey: 'offensiveRebounds', tooltipText: 'Offensive Rebounds' },
                { title: 'DREB', dataKey: 'defensiveRebounds', tooltipText: 'Defensive Rebounds' },
                { title: 'TO', dataKey: 'turnovers', tooltipText: 'Turnovers' },
            ],
            data: [
                {
                    name: '',
                    minutes: boldTextComponent('-'),
                    points: boldTextComponent(boxscore_totals.points),
                    rebounds: boldTextComponent(boxscore_totals.rebounds_total),
                    assists: boldTextComponent(boxscore_totals.assists),
                    steals: boldTextComponent(boxscore_totals.steals),
                    blocks: boldTextComponent(boxscore_totals.blocked_shots),
                    fieldGoals: boldTextComponent(
                        boxscore_totals.field_goals_made +
                            '-' +
                            boxscore_totals.field_goals_attempted
                    ),
                    threePointers: boldTextComponent(
                        boxscore_totals.three_pointers_made +
                            '-' +
                            boxscore_totals.three_pointers_attempted
                    ),
                    freeThrows: boldTextComponent(
                        boxscore_totals.free_throws_made +
                            '-' +
                            boxscore_totals.free_throws_attempted
                    ),
                    offensiveRebounds: boldTextComponent(boxscore_totals.offensive_rebounds),
                    defensiveRebounds: boldTextComponent(boxscore_totals.defensive_rebounds),
                    personalFouls: boldTextComponent(boxscore_totals.personal_fouls),
                    turnovers: boldTextComponent(boxscore_totals.turnovers),
                },
            ],
        },
    ]
}

export function formatDNP(players = []) {
    const dnpPlayers = players.filter(player => player.reason === 'DNP')
    const formattedPlayerNames = dnpPlayers.map(player => {
        return `${player.first_name} ${player.last_name}`
    })
    return formattedPlayerNames.join(', ')
}
export function formatInactive(players = []) {
    const dnpPlayers = players.filter(player => player.reason === 'INACTIVE')
    const formattedPlayerNames = dnpPlayers.map(player => {
        return `${player.first_name} ${player.last_name}`
    })
    return formattedPlayerNames.join(', ')
}

export function isGameTypePostseason(gameType) {
    switch (gameType) {
        case 'Conference Finals':
        case 'First Round':
        case 'Conference Semifinals':
        case 'NBA Finals':
            return true
        default:
            return false
    }
}

export function formatGamecardData(game = {}, options = {}) {
    const gameStatus = game.game_status
    const {
        visitingTeamName,
        visitingTeamLogo,
        homeTeamName,
        homeTeamLogo,
        broadcastName,
        broadcastString,
        gameLoser,
        gameTime,
        gameDate,
    } = formatGameTeamDetails(game)

    const footerText =
        game?.cup_round || game?.type === 'NBA Cup Finals'
            ? formatNBACupRoundLabel(game, game.home_team, game.visiting_team)
            : game.series_matchups_result

    const commonProps = {
        visitingTeamName,
        visitingTeamLogo,
        homeTeamName,
        homeTeamLogo,
        link: preserveQueryParams(`${game.new_game_url}`),
    }

    if (gameStatus === 'Pre-Game') {
        return {
            ...commonProps,
            visitingTeamData: game.new_visiting_odds || '\u00A0',
            homeTeamData: game.new_home_odds || '\u00A0',
            teamDataStyle: 'odds',
            footerText: footerText,
            renderGameStatus: () => {
                return (
                    <>
                        {options.showDate ? (
                            <div className="game_date">
                                {gameDate}, {gameTime}
                            </div>
                        ) : (
                            <div className="game_date">{gameTime}</div>
                        )}
                        {broadcastName && <div className="broadcastName">{broadcastName}</div>}
                    </>
                )
            },
        }
    }
    if (gameStatus === 'In-Progress') {
        return {
            ...commonProps,
            visitingTeamData: `${game.visiting_team?.score}`,
            homeTeamData: `${game.home_team?.score}`,
            footerText: broadcastString ? broadcastString : footerText,
            renderGameStatus: () => {
                return (
                    <>
                        {game.new_status_long.split(' ').map((statusItem, index) => {
                            return <div key={index}>{statusItem}</div>
                        })}
                    </>
                )
            },
        }
    }
    if (gameStatus === 'Final') {
        return {
            ...commonProps,
            visitingTeamData: `${game.visiting_team?.score}`,
            homeTeamData: `${game.home_team?.score}`,
            loser: gameLoser,
            footerText: footerText,
            renderGameStatus: () => <div>{game.new_status_long}</div>,
        }
    }

    // For games that are cancelled, postponed, forfeit, etc...
    return {
        ...commonProps,
        visitingTeamData: '\u00A0',
        homeTeamData: '\u00A0',
        footerText: footerText,
        renderGameStatus: () => {
            return (
                <>
                    <div>{gameStatus}</div>
                </>
            )
        },
    }
}

export function formatDivisionRank(team) {
    if (team.division.rank) {
        return `${formatDivisionRanksNumericValue(team.division.rank)} ${team.division.name}`
    } else {
        return ''
    }
}

function boldTextComponent(text) {
    return (
        <div
            style={{
                fontWeight: 'bold',
            }}
        >
            {text}
        </div>
    )
}

export function formatNBACupRoundLabel(details, homeTeam, visitingTeam) {
    const homeTeamShortGroup = formatCupGroupShortname(homeTeam)
    const visitingTeamShortGroup = formatCupGroupShortname(visitingTeam)

    if (details.cup_round && details.cup_round === 1 && homeTeam.cup_group) {
        return `NBA Cup Round  ${details.cup_round} - ${homeTeam.cup_group}`
    } else if (details.cup_round && (details.cup_round === 2 || details.cup_round === 3)) {
        return (
            'NBA Cup Round ' +
            details.cup_round +
            (visitingTeamShortGroup && homeTeamShortGroup
                ? ' - ' + visitingTeamShortGroup + ' vs ' + homeTeamShortGroup
                : '')
        )
    } else if (details.type && details.type === 'NBA Cup Finals') {
        return 'NBA Cup Finals'
    } else {
        return details.series_matchups_result
    }
}

function formatCupGroupShortname(team) {
    switch (team?.cup_group) {
        case 'East Group A':
            return 'East A' + team.cup_place
        case 'East Group B':
            return 'East B' + team.cup_place
        case 'East Group C':
            return 'East C' + team.cup_place
        case 'West Group A':
            return 'West A' + team.cup_place
        case 'West Group B':
            return 'West B' + team.cup_place
        case 'West Group C':
            return 'West C' + team.cup_place
        default:
            return ''
    }
}

export function overrideAllStarsTeamName(team) {
    switch (team?.short_name) {
        case 'WCS':
            return 'West All-Stars'
        case 'ECS':
            return 'East All-Stars'
        default:
            return team.name
    }
}

export function overrideAllStarsTeamShortName(team) {
    switch (team?.short_name) {
        case 'WCS':
            return 'WES'
        case 'ECS':
            return 'EAS'
        default:
            return team.short_name
    }
}


const playerLegendItems = [
  {
    name: "GP",
    description: "Games Played",
  },
  {
    name: "GS",
    description: "Games Started",
  },
  {
    name: "MIN",
    description: "Minutes Played (Average)",
  },
  {
    name: "PTS",
    description: "Points (Average)",
  },
  {
    name: "REB",
    description: "Rebounds (Average)",
  },
  {
    name: "AST",
    description: "Assists (Average)",
  },
  {
    name: "FG%",
    description: "Field Goal Percentage",
  },
  {
    name: "3PT%",
    description: "Three Point Percentage",
  },
  {
    name: "FT%",
    description: "Free Throw Percentage",
  },
  {
    name: "STL",
    description: "Steals (Average)",
  },
  {
    name: "BLK",
    description: "Blocks (Average)",
  },
  {
    name: "TO",
    description: "Turnovers (Average)",
  },
]

export const statsLegendSections = [
  {
    title: "Players",
    items: playerLegendItems,
  },
]

function formatPlayerStats(stats = []) {
  const playerColumns = [
    {
      title: "PLAYER",
      dataKey: "player",
      textAlign: "left",
      cellWidthOverride: "20",
      initialSortDirection: "asc",
      customSortFunctionAscending: (a, b) => sortByPlayerElement(a.player, b.player),
      customSortFunctionDescending: (a, b) => sortByPlayerElement(b.player, a.player),
      sticky: true,
    },
    {
      title: "GP",
      dataKey: "gamesPlayed",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "GS",
      dataKey: "gamesStarted",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "MIN",
      dataKey: "minutes",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "PTS",
      dataKey: "points",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "REB",
      dataKey: "rebounds",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "AST",
      dataKey: "assists",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "FG%",
      dataKey: "fieldGoalPercent",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "3PT%",
      dataKey: "threePointPercent",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "FT%",
      dataKey: "freeThrowPercent",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "STL",
      dataKey: "steals",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "BLK",
      dataKey: "blocks",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "desc",
    },
    {
      title: "TO",
      dataKey: "turnovers",
      textAlign: "center",
      cellWidthOverride: "5",
      initialSortDirection: "asc",
    },
  ]

  const playerData = stats.map((player) => {
    return {
      player: formatPlayerLink(player, { position: player.short_position }),
      gamesPlayed: player.games_played,
      gamesStarted: player.games_started,
      minutes: player.minutes_average,
      points: player.points_per_game,
      rebounds: player.rebounds_per_game,
      assists: player.assists_per_game,
      fieldGoalPercent: player.field_goals_pct,
      threePointPercent: player.three_point_goals_pct,
      freeThrowPercent: player.free_throw_pct,
      steals: player.steals_per_game,
      blocks: player.blocks_per_game,
      turnovers: player.turnovers_per_game,
    }
  })

  return {
    columns: playerColumns,
    data: playerData,
  }
}

export function formatFullSeasonStats(stats) {
  return [
    {
      title: "Players",
      initialSortColumn: "points",
      initialSortReverse: true,
      ...formatPlayerStats(stats?.players),
    },
  ]
}
